/**
 * Trade winds — a deterministic, PURE speed multiplier applied inside stepShipBody on BOTH the server
 * and the client's local-ship prediction, so it can never desync. A circulating current runs the triangle
 * of outposts (OUTPOSTS[i] → OUTPOSTS[i+1], wrapping): a ship inside a lane and sailing WITH the current
 * gains up to TRADE_WIND.BOOST extra forward speed, proportional to heading alignment (against = nothing).
 *
 * The lane geometry is precomputed ONCE from OUTPOSTS × WORLD_SIZE (both compile-time constants), so the
 * hot path is allocation-free and identical on every machine — the same numbers the Room spawns outposts
 * from, so server truth and client prediction read one source (zero drift). No RNG, no wall-clock.
 */
import { OUTPOSTS, TRADE_WIND, WORLD_SIZE } from '@piratas/protocol';

interface Lane { ax: number; ay: number; dx: number; dy: number; len: number } // origin + unit direction + length

/** The three directed lanes of the outpost triangle (a→b, wrapping). Built once at module load. */
const LANES: Lane[] = (() => {
  const pts = OUTPOSTS.map((o) => ({ x: o.fx * WORLD_SIZE, y: o.fy * WORLD_SIZE }));
  const out: Lane[] = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]!, b = pts[(i + 1) % pts.length]!;
    const dx = b.x - a.x, dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    out.push({ ax: a.x, ay: a.y, dx: dx / len, dy: dy / len, len });
  }
  return out;
})();

const W2 = TRADE_WIND.LANE_WIDTH * TRADE_WIND.LANE_WIDTH;

/**
 * Forward-speed multiplier at (x,y) for a ship heading `heading` (radians). 1 = no boost; up to
 * (1 + TRADE_WIND.BOOST) sailing dead-aligned with a lane's current. Pure + deterministic — the ONLY
 * source of the trade-wind speedup, called identically by the server sim and client prediction.
 */
export function tradeWindMul(x: number, y: number, heading: number): number {
  const hx = Math.cos(heading), hy = Math.sin(heading);
  let best = 1;
  for (let i = 0; i < LANES.length; i++) {
    const L = LANES[i]!;
    const rx = x - L.ax, ry = y - L.ay;
    const t = rx * L.dx + ry * L.dy;        // projection distance along the lane
    if (t < 0 || t > L.len) continue;        // beyond the segment's ends (outposts are the endpoints)
    const px = L.ax + L.dx * t, py = L.ay + L.dy * t;
    const ddx = x - px, ddy = y - py;
    if (ddx * ddx + ddy * ddy > W2) continue; // outside the lane corridor
    const align = hx * L.dx + hy * L.dy;      // dot(heading, laneDir): +1 with the flow, ≤0 against it
    const a = align < 0 ? 0 : align > 1 ? 1 : align;
    const mul = 1 + TRADE_WIND.BOOST * a;
    if (mul > best) best = mul;
  }
  return best;
}

/**
 * The unit direction of the trade current at (x,y) — the lane the point sits in (nearest by perpendicular
 * distance), or null if outside every lane. CLIENT-FX only (foam/particle advection); shares the SAME lane
 * geometry as tradeWindMul so the visible current and the actual boost always agree.
 */
export function tradeLaneDirAt(x: number, y: number): { dx: number; dy: number } | null {
  let best: { dx: number; dy: number } | null = null;
  let bestD2 = W2;
  for (let i = 0; i < LANES.length; i++) {
    const L = LANES[i]!;
    const rx = x - L.ax, ry = y - L.ay;
    const t = rx * L.dx + ry * L.dy;
    if (t < 0 || t > L.len) continue;
    const px = L.ax + L.dx * t, py = L.ay + L.dy * t;
    const ddx = x - px, ddy = y - py;
    const d2 = ddx * ddx + ddy * ddy;
    if (d2 <= bestD2) { bestD2 = d2; best = { dx: L.dx, dy: L.dy }; }
  }
  return best;
}
